// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";

// Constants
const INK = "#202338";
const INK_2 = "#5B6D7A";
const WARM = "#E2772E";
const BAD = "#E10C0C";
const LINE = "#5B6D7A80";

const WIDTH = 600;
const HEIGHT = 162;
const LEFT = 36;
// A wide right margin, reserved for the two median labels. Without it they print on top of the
// last pair of bars and their values.
const RIGHT = 70;
const MID_Y = 80;
const SPAN = 52;
const MAX_BAR_WIDTH = 26;

export type FacilityHalfType = {
  // "BaseOut" makes the eyes converge; "BaseIn" lets them relax.
  direction: string;
  // Null on a flip the patient never cleared. A zero would read as an instant one.
  secondsToClear: number | null;
};

type FacilityChartType = {
  halves: Array<FacilityHalfType>;
  medianBaseOutSeconds: number | null;
  medianBaseInSeconds: number | null;
};

const isBaseOut = (direction: string): boolean => /baseout/i.test(direction);

/**
 * Every flip of the prism, in the order it was shown, with the time the patient took on each.
 *
 * Bars above the line are the base-out flips, which make the eyes converge, and bars below are
 * the base-in ones. Drawing them on opposite sides makes the asymmetry between the two
 * directions a shape rather than a pair of numbers to subtract.
 *
 * The dashed line across each half is the MEDIAN, which is what the exam measures
 * (`MedianSecondsToClear`). Calling it an average would be wrong by a word that matters here:
 * one flip the patient laboured over drags a mean and does not drag this.
 */
const FacilityChart = ({
  halves,
  medianBaseOutSeconds,
  medianBaseInSeconds,
}: FacilityChartType) => {
  const { t } = useTranslation();

  // The tallest bar, with headroom, so a cleared flip never touches the axis label above it.
  const ceiling = useMemo(
    () => Math.max(...halves.map((half) => half.secondsToClear ?? 0), 1) * 1.15,
    [halves]
  );

  const slot = (WIDTH - LEFT - RIGHT) / Math.max(halves.length, 1);
  const barWidth = Math.min(slot - 6, MAX_BAR_WIDTH);
  const toHeight = (seconds: number) => (seconds / ceiling) * SPAN;

  const medians = [
    {
      key: "baseOut",
      seconds: medianBaseOutSeconds,
      up: true,
    },
    {
      key: "baseIn",
      seconds: medianBaseInSeconds,
      up: false,
    },
  ];

  return (
    <Box
      component="svg"
      role="img"
      data-testid="synoptophore-facility-chart"
      aria-label={t("Each flip of the prism, in order")}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      sx={{ display: "block", width: "100%", mx: "auto" }}
    >
      <line
        x1={LEFT - 6}
        y1={MID_Y}
        x2={WIDTH - RIGHT + 2}
        y2={MID_Y}
        stroke={LINE}
      />

      {halves.map((half, index) => {
        const x = LEFT + index * slot + (slot - barWidth) / 2;
        const up = isBaseOut(half.direction);
        const key = `${index}-${half.direction}`;

        if (half.secondsToClear === null) {
          const y = up ? MID_Y - SPAN : MID_Y;
          const crossX = x + barWidth / 2;
          const crossY = MID_Y + (up ? -1 : 1) * (SPAN / 2);

          return (
            <g key={key}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={SPAN}
                rx={2}
                fill="none"
                stroke={BAD}
                strokeDasharray="3 2"
              />
              <path
                d={`M${crossX - 3.5} ${crossY - 3.5} l7 7 M${crossX + 3.5} ${
                  crossY - 3.5
                } l-7 7`}
                stroke={BAD}
                strokeWidth={1.8}
                fill="none"
              />
            </g>
          );
        }

        const height = toHeight(half.secondsToClear);
        const y = up ? MID_Y - height : MID_Y;

        return (
          <g key={key}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={height}
              rx={2}
              fill={WARM}
              opacity={up ? 1 : 0.55}
            />
            <text
              x={x + barWidth / 2}
              y={up ? y - 4 : y + height + 11}
              fontSize="9"
              fill={INK_2}
              textAnchor="middle"
            >
              {half.secondsToClear.toFixed(1)}
            </text>
          </g>
        );
      })}

      {medians.map((median) =>
        median.seconds === null ? null : (
          <g key={median.key}>
            <line
              x1={LEFT - 6}
              y1={MID_Y + (median.up ? -1 : 1) * toHeight(median.seconds)}
              x2={WIDTH - RIGHT + 2}
              y2={MID_Y + (median.up ? -1 : 1) * toHeight(median.seconds)}
              stroke={INK}
              strokeWidth={1.6}
              strokeDasharray="5 3"
            />
            <text
              x={WIDTH - RIGHT + 8}
              y={MID_Y + (median.up ? -1 : 1) * toHeight(median.seconds) + 3.2}
              fontSize="9"
              fill={INK}
            >
              {t("median {{seconds}} s", {
                seconds: median.seconds.toFixed(1),
              })}
            </text>
          </g>
        )
      )}

      {/* Base out and base in are what the prism is doing and what the payload records
          (`FacilityDirection`); the gloss says what the eyes do about it. The axes are NOT
          near and far -- the target never moves and the focus never changes. */}
      <text x={2} y={14} fontSize="9" fill={INK_2}>
        {t("base out · eyes converge")}
      </text>
      <text x={2} y={HEIGHT - 6} fontSize="9" fill={INK_2}>
        {t("base in · eyes relax")}
      </text>
      <text x={LEFT - 6} y={HEIGHT - 24} fontSize="9" fill={INK_2}>
        {t("start of the run")}
      </text>
      <text
        x={WIDTH - RIGHT + 2}
        y={HEIGHT - 24}
        fontSize="9"
        fill={INK_2}
        textAnchor="end"
      >
        {t("end of the run")}
      </text>
    </Box>
  );
};

export default FacilityChart;
